// ImportExport — T061
import React, { useState, useRef } from 'react';
import { exportMonitors, importMonitors } from '../services/api';

export default function ImportExport() {
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [result, setResult] = useState<{ created: number; updated: number; failed: number; errors: string[] } | null>(null);
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleExport = async () => {
    setExporting(true);
    setError('');
    try {
      const csv = await exportMonitors();
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `monitors-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setExporting(false);
    }
  };

  const handleFile = async (file: File) => {
    if (!file.name.endsWith('.csv')) {
      setError('Please select a CSV file');
      return;
    }
    setImporting(true);
    setError('');
    setResult(null);
    try {
      const text = await file.text();
      const res = await importMonitors(text);
      setResult(res);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setImporting(false);
    }
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  return (
    <div className="max-w-lg mx-auto space-y-6">
      <h1 className="text-white text-h1">Import / Export</h1>

      {/* Export */}
      <div className="bg-surface rounded-lg p-6 border border-border">
        <h2 className="text-text-primary font-semibold mb-2">Export Monitors</h2>
        <p className="text-text-secondary text-sm mb-4">Download all monitors as a CSV file.</p>
        <button
          onClick={handleExport}
          disabled={exporting}
          className="bg-brand text-white rounded px-4 py-2 text-sm min-h-[44px] disabled:opacity-50 hover:bg-brand-hover transition-colors"
        >
          {exporting ? 'Exporting...' : 'Download CSV'}
        </button>
      </div>

      {/* Import */}
      <div className="bg-surface rounded-lg p-6 border border-border">
        <h2 className="text-text-primary font-semibold mb-2">Import Monitors</h2>
        <p className="text-text-secondary text-sm mb-4">Upload a CSV to create monitors in bulk.</p>

        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
            dragOver ? 'border-brand bg-brand/10' : 'border-border hover:border-text-muted'
          }`}
        >
          <p className="text-text-secondary text-sm">
            {importing ? 'Importing...' : 'Drag & drop a CSV file here, or click to browse'}
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            aria-label="CSV file upload"
            className="hidden"
            onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
          />
        </div>
      </div>

      {result && (
        <div className="bg-surface rounded-lg p-4 border border-border">
          <h3 className="text-text-primary font-semibold mb-2">Import Result</h3>
          <p className="text-status-up text-sm">Created: {result.created}</p>
          <p className="text-brand text-sm">Updated: {result.updated}</p>
          {result.failed > 0 && <p className="text-status-down text-sm">Failed: {result.failed}</p>}
          {result.errors.length > 0 && (
            <div className="mt-2 space-y-1">
              {result.errors.map((err, i) => (
                <p key={i} className="text-status-down text-xs">{typeof err === 'string' ? err : JSON.stringify(err)}</p>
              ))}
            </div>
          )}
        </div>
      )}

      {error && <div className="bg-status-down/20 text-status-down rounded px-4 py-3 text-sm">{error}</div>}
    </div>
  );
}
